/**
 * Studio.jsx — Founder-side Studios MARKETPLACE (Sprint 1).
 *
 * Replaces the Sprint-0 placeholder.  Lives at /startup/studio.
 *
 *   - Hero + "Become a Studios agency" CTA in the corner
 *   - Filters (search, sector, services, max-budget, stage) — persisted in URL
 *   - Agency cards w/ rule-based match score + tooltip
 *   - Shortlist (max 3) → side-by-side compare drawer
 *   - Click card → /startup/studio/agency/:id (AgencyProfile)
 *   - Legacy orders archive callout (unchanged from Sprint 0)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SECTORS } from '@/utils/sector_options';
import { toast } from 'sonner';
import {
  Sparkle, ArrowRight, Storefront, Archive, Spinner, MagnifyingGlass,
  CaretDown, Plus, X, Check, Star, ShieldCheck, Buildings,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SERVICES = [
  'linkedin_ghostwriting', 'founder_content', 'brand_identity', 'investor_decks',
  'short_form_video', 'instagram_reels', 'blog_writing', 'newsletter',
  'press_release', 'web_design', 'performance_marketing', 'seo',
];
const BUDGETS = [
  { v: 30000,  l: '< ₹30k' },
  { v: 60000,  l: '< ₹60k' },
  { v: 100000, l: '< ₹1L' },
  { v: 200000, l: '< ₹2L' },
];

const Studio = ({ user, hideHeader = false }) => {
  const accent = user?.role === 'startup' ? '#10b981' : '#6366f1';
  const [agencies, setAgencies] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [archivedOrderCount, setArchivedOrderCount] = useState(0);
  const [shortlist, setShortlist] = useState([]); // array of agency_id
  const [showCompare, setShowCompare] = useState(false);
  const [activeEng, setActiveEng] = useState(null);

  // Filters
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('');
  const [service, setService] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [stage, setStage] = useState('');

  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Initial load
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/agencies`, { headers }),
      axios.get(`${API}/agencies/match-scores`, { headers }).catch(() => ({ data: { scores: {} } })),
      axios.get(`${API}/studio/orders`, { headers }).catch(() => ({ data: { orders: [] } })),
      axios.get(`${API}/engagements/mine`, { headers }).catch(() => ({ data: { engagements: [] } })),
    ]).then(([a, ms, o, e]) => {
      setAgencies(a.data.agencies || []);
      setScores(ms.data.scores || {});
      setArchivedOrderCount((o.data.orders || []).length);
      const active = (e.data.engagements || []).find((x) => ['draft','negotiating','active','paused'].includes(x.status));
      setActiveEng(active || null);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  // Client-side filter on top of the server fetch (cheap; server already cuts by status=approved)
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return agencies.filter((a) => {
      if (needle && !((a.name || '') + ' ' + (a.tagline || '') + ' ' + (a.description || '')).toLowerCase().includes(needle)) return false;
      if (sector && !(a.sector_focus || []).includes(sector)) return false;
      if (service && !(a.service_tags || []).includes(service)) return false;
      if (stage && !(a.stage_focus || []).includes(stage)) return false;
      if (maxBudget && (a.pricing_band_min_inr || 0) > Number(maxBudget)) return false;
      return true;
    }).sort((a, b) => (scores[b.id]?.score || 0) - (scores[a.id]?.score || 0));
  }, [agencies, scores, q, sector, service, stage, maxBudget]);

  const toggleShortlist = (id) => {
    setShortlist((p) => {
      if (p.includes(id)) return p.filter((x) => x !== id);
      if (p.length >= 3) {
        toast.error('You can shortlist up to 3 agencies. Remove one to add another.');
        return p;
      }
      return [...p, id];
    });
  };

  const clearFilters = () => { setQ(''); setSector(''); setService(''); setMaxBudget(''); setStage(''); };
  const activeFilters = [sector, service, maxBudget, stage].filter(Boolean).length + (q ? 1 : 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="studio-marketplace-page">
      {/* Header row */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        {!hideHeader && <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>Founderboard Studios</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-text-primary leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
            Find an agency built for your stage
          </h1>
          <p className="text-base text-text-secondary max-w-2xl leading-relaxed">
            Browse verified marketing &amp; creative agencies, compare match scores, book intro
            calls, and run the entire engagement inside Founderboard.
          </p>
        </div>}
        <button
          onClick={() => navigate('/studio/apply')}
          data-testid="studio-become-agency-btn"
          className="self-start lg:self-auto inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-text-primary text-text-secondary hover:text-text-primary px-3 py-2 rounded-lg transition-colors"
        >
          <Buildings size={13} weight="bold" /> Become a Studios agency <ArrowRight size={11} weight="bold" />
        </button>
      </div>

      {/* Active engagement banner */}
      {activeEng && (
        <button
          onClick={() => navigate(`/startup/marketing/engagements/${activeEng.id}`)}
          data-testid="studio-active-engagement-banner"
          className="w-full mb-6 bg-surface border border-border rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:border-text-primary transition-colors text-left"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>Active engagement</div>
            <div className="text-base font-heading font-bold text-text-primary">{activeEng.agency_name}</div>
            <div className="text-[11px] text-text-muted">Brief: {(activeEng.brief_status || '—').replace(/_/g, ' ')} · SOW: {(activeEng.sow_status || '—').replace(/_/g, ' ')} · Status: {activeEng.status}</div>
          </div>
          <span className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: accent }}>
            Open workspace <ArrowRight size={11} weight="bold" />
          </span>
        </button>
      )}

      {/* Filter bar */}
      <div className="bg-surface border border-border rounded-xl p-3 mb-6" data-testid="studio-filters">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search agencies, services, sectors…"
              data-testid="studio-filter-q"
              className="w-full bg-background border border-border rounded-md pl-9 pr-3 py-2 text-sm placeholder:text-text-muted focus:outline-none focus:border-text-primary"
            />
          </div>
          <FilterSelect value={sector} onChange={setSector} placeholder="Sector" options={SECTORS} dt="studio-filter-sector" />
          <FilterSelect value={service} onChange={setService} placeholder="Service" options={SERVICES} dt="studio-filter-service" labelMap={(v) => v.replace(/_/g, ' ')} />
          <FilterSelect value={maxBudget} onChange={setMaxBudget} placeholder="Budget" options={BUDGETS.map(b => b.v)} dt="studio-filter-budget" labelMap={(v) => BUDGETS.find(b => b.v === Number(v))?.l || v} />
          <FilterSelect value={stage} onChange={setStage} placeholder="Stage" options={['pre_seed', 'seed', 'series_a']} dt="studio-filter-stage" labelMap={(v) => v.replace(/_/g, ' ')} />
          {activeFilters > 0 && (
            <button onClick={clearFilters} className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 inline-flex items-center gap-1" data-testid="studio-filter-clear">
              <X size={11} weight="bold" /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {/* Results header */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted">
          {loading ? 'Loading…' : `${filtered.length} agenc${filtered.length === 1 ? 'y' : 'ies'}`}
        </div>
        {shortlist.length > 0 && (
          <button
            onClick={() => setShowCompare(true)}
            data-testid="studio-compare-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Compare {shortlist.length} <ArrowRight size={11} weight="bold" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-text-muted flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
          <Storefront size={24} className="mx-auto mb-2 text-text-muted/40" /> No agencies match these filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="studio-marketplace-grid">
          {filtered.map((a) => (
            <AgencyCard
              key={a.id}
              agency={a}
              match={scores[a.id]}
              shortlisted={shortlist.includes(a.id)}
              onToggleShortlist={() => toggleShortlist(a.id)}
              onOpen={() => navigate(`/startup/marketing/agency/${a.id}`)}
              accent={accent}
            />
          ))}
        </div>
      )}

      {/* Legacy archive callout */}
      {archivedOrderCount > 0 && (
        <div className="mt-10 pt-6 border-t border-border" data-testid="studio-archive-link">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">Legacy Studios history</div>
          <div className="flex items-center justify-between gap-3 bg-surface border border-border rounded-lg px-4 py-3">
            <div>
              <div className="text-sm text-text-primary font-medium">
                You have {archivedOrderCount} archived order{archivedOrderCount === 1 ? '' : 's'} from the old Bundles shell.
              </div>
              <div className="text-[11px] text-text-muted">Read-only history. New work happens on the marketplace.</div>
            </div>
          </div>
        </div>
      )}

      {showCompare && (
        <CompareDrawer
          accent={accent}
          agencies={filtered.filter((a) => shortlist.includes(a.id))}
          scores={scores}
          onClose={() => setShowCompare(false)}
          onRemove={toggleShortlist}
          onOpen={(id) => { setShowCompare(false); navigate(`/startup/marketing/agency/${id}`); }}
        />
      )}
    </div>
  );
};

const FilterSelect = ({ value, onChange, placeholder, options, dt, labelMap }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={dt}
      className="appearance-none bg-background border border-border rounded-md pl-3 pr-7 py-2 text-sm text-text-primary focus:outline-none focus:border-text-primary cursor-pointer"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (<option key={String(o)} value={o}>{labelMap ? labelMap(o) : o}</option>))}
    </select>
    <CaretDown size={10} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
  </div>
);

const AgencyCard = ({ agency, match, shortlisted, onToggleShortlist, onOpen, accent }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className="bg-surface border border-border rounded-xl p-5 flex flex-col hover:border-text-primary transition-colors group cursor-pointer"
      data-testid={`studio-agency-card-${agency.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {agency.verified && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
              <ShieldCheck size={11} weight="fill" /> Verified
            </span>
          )}
          {agency.turnaround_days && (
            <span className="text-[10px] text-text-muted">· {agency.turnaround_days}d avg turnaround</span>
          )}
        </div>
        {match && (
          <div
            className="relative cursor-help"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={(e) => { e.stopPropagation(); setShowTooltip((p) => !p); }}
            data-testid={`studio-agency-match-${agency.id}`}
          >
            <div
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
              style={{ backgroundColor: `${accent}1a`, color: accent }}
            >
              {match.score}% match
            </div>
            {showTooltip && (
              <div className="absolute right-0 top-full mt-1.5 z-20 w-64 bg-background border border-border rounded-lg p-3 shadow-lg" data-testid={`studio-agency-match-tooltip-${agency.id}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">Why this match?</div>
                <ul className="space-y-1">
                  {(match.reasons || []).map((r, i) => (
                    <li key={i} className="text-[11px] text-text-secondary flex items-start gap-1.5">
                      <span className="text-text-primary mt-1">·</span><span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="text-left">
        <h3 className="font-heading font-bold text-text-primary text-lg mb-1 group-hover:underline" style={{ letterSpacing: '-0.01em' }}>{agency.name}</h3>
        <p className="text-xs text-text-secondary mb-3 line-clamp-2">{agency.tagline}</p>
      </button>
      <div className="flex flex-wrap gap-1 mb-3 flex-1">
        {(agency.service_tags || []).slice(0, 4).map((t) => (
          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-background border border-border text-text-secondary">{t.replace(/_/g, ' ')}</span>
        ))}
        {(agency.service_tags || []).length > 4 && (
          <span className="text-[10px] text-text-muted self-center">+{agency.service_tags.length - 4}</span>
        )}
      </div>
      {agency.review_count > 0 && (
        <div className="flex items-center gap-1.5 mb-3 text-[11px] text-text-secondary">
          <Star size={11} weight="fill" className="text-amber-500" />
          <span className="font-mono font-semibold text-text-primary">{agency.review_avg?.toFixed(1)}</span>
          <span className="text-text-muted">· {agency.review_count} verified review{agency.review_count === 1 ? '' : 's'}</span>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm font-mono font-semibold text-text-primary">{agency.pricing_display || '—'}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleShortlist(); }}
          data-testid={`studio-agency-shortlist-${agency.id}`}
          className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border transition-colors ${shortlisted ? 'text-white' : 'text-text-secondary border-border hover:border-text-primary'}`}
          style={shortlisted ? { backgroundColor: accent, borderColor: accent } : {}}
        >
          {shortlisted ? <><Check size={10} weight="bold" /> Shortlisted</> : <><Plus size={10} weight="bold" /> Shortlist</>}
        </button>
      </div>
    </div>
  );
};

const CompareDrawer = ({ accent, agencies, scores, onClose, onRemove, onOpen }) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-stretch justify-end" onClick={onClose} data-testid="studio-compare-drawer">
    <div className="bg-surface w-full max-w-5xl h-full overflow-y-auto border-l border-border" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-surface z-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: accent }}>Compare</div>
          <h3 className="font-heading font-bold text-text-primary text-lg">Side by side</h3>
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={18} weight="bold" /></button>
      </div>
      <div className="grid p-5 gap-4" style={{ gridTemplateColumns: `repeat(${agencies.length}, minmax(0, 1fr))` }}>
        {agencies.map((a) => (
          <div key={a.id} className="border border-border rounded-xl p-4 flex flex-col" data-testid={`studio-compare-card-${a.id}`}>
            <div className="flex items-start justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>{scores[a.id]?.score || 0}% match</span>
              <button onClick={() => onRemove(a.id)} className="text-text-muted hover:text-danger text-[10px]" data-testid={`studio-compare-remove-${a.id}`}>Remove</button>
            </div>
            <h4 className="font-heading font-bold text-text-primary text-base mb-1">{a.name}</h4>
            <p className="text-[11px] text-text-secondary mb-3 line-clamp-3">{a.tagline}</p>
            <CompareRow label="Pricing" value={a.pricing_display || '—'} />
            <CompareRow label="Turnaround" value={a.turnaround_days ? `${a.turnaround_days} days` : '—'} />
            <CompareRow label="Sectors" value={(a.sector_focus || []).slice(0, 3).join(', ') || '—'} />
            <CompareRow label="Services" value={`${(a.service_tags || []).length} listed`} />
            <CompareRow label="Reviews" value={a.review_count ? `${a.review_avg?.toFixed(1)}★ (${a.review_count})` : 'New'} />
            <button
              onClick={() => onOpen(a.id)}
              data-testid={`studio-compare-open-${a.id}`}
              className="mt-3 px-3 py-2 text-xs font-semibold rounded-md text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: accent }}
            >
              View profile →
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CompareRow = ({ label, value }) => (
  <div className="flex justify-between text-[11px] py-1.5 border-t border-border">
    <span className="text-text-muted">{label}</span>
    <span className="text-text-primary font-medium text-right truncate ml-2 max-w-[60%]">{value}</span>
  </div>
);

export default Studio;
